import { useId, type ReactNode } from 'react';

type TextFieldProps = {
  label: string;
  value: string;
  on_change: (value: string) => void;
  type?: string;
  autocomplete?: string;
  error?: string | null;
  disabled?: boolean;
  on_enter?: () => void;
};

/**
 * Labelled text input styled for the dark /welcome surface. Associates the
 * label with the input and exposes validation errors to assistive tech.
 *
 * @param label - Field label.
 * @param value - Controlled value.
 * @param on_change - Called with the new value on input.
 * @param type - Input type (default 'text').
 * @param autocomplete - Native autocomplete hint.
 * @param error - Error message to show, or null/empty for none.
 * @param disabled - Whether the input is disabled.
 * @param on_enter - Called when Enter is pressed in the field.
 */
export function TextField({
  label,
  value,
  on_change,
  type = 'text',
  autocomplete,
  error,
  disabled,
  on_enter,
}: TextFieldProps): ReactNode {
  const id = useId();
  const has_error = error !== null && error !== undefined && error !== '';
  return (
    <div className={has_error === true ? 'uf-field uf-field--error' : 'uf-field'}>
      <label className="uf-field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="uf-input"
        type={type}
        value={value}
        autoComplete={autocomplete}
        disabled={disabled}
        aria-invalid={has_error === true ? true : undefined}
        onChange={(e) => on_change(e.target.value)}
        onKeyDown={
          on_enter === undefined
            ? undefined
            : (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  on_enter();
                }
              }
        }
      />
      {has_error === true ? (
        <span className="uf-field__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
